import { retryableWorkshopReviewErrors } from '@mio/shared';
import { useEffect, useRef, useState } from 'react';

import { scrollWithinPane } from '@/shared/lib/scroll-within-pane';
import { Alert } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { Pill } from '@/shared/ui/pill';
import { ProgressBar } from '@/shared/ui/progress-bar';
import { Skeleton } from '@/shared/ui/skeleton';
import { LoadingStatus } from '@/shared/ui/spinner';

import { reviewFailureMessage } from '../review-failure';

import { WorkshopReviewHistory } from './workshop-review-history';
import { WorkshopSolution } from './workshop-solution';

import type { SolutionQuery } from './workshop-solution';
import type { DraftState } from '../api/explanation-draft';
import type { useExplanationDraft } from '../api/use-explanation-draft';
import type { useWorkshopEvidence } from '../api/use-workshop-session';
import type { WorkshopSession, WorkshopSubmission } from '@mio/shared';
import type { FormEvent } from 'react';

function elapsedLabel(since: string, now: number) {
  const seconds = Math.max(0, Math.floor((now - new Date(since).getTime()) / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

function outcomeAnnouncement(submission: WorkshopSubmission) {
  switch (submission.status) {
    case 'passed':
      return 'Your review is ready: you passed this workshop.';
    case 'revision_requested':
      return 'Your review is ready: revision requested.';
    case 'failed':
      return 'The review could not finish. Your submission is saved.';
    default:
      return null;
  }
}

/** "Saved" only means something once there is a saved explanation; an empty draft stays quiet. */
function draftStatusLabel(draft: Pick<DraftState, 'status' | 'text'>) {
  if (draft.status !== 'saved') return 'Explanation has unsaved changes.';
  return draft.text.trim() ? 'Explanation saved.' : null;
}

export function WorkshopAssessment({
  task,
  workspace,
  evidence,
  draft,
  busy,
  draftBlocked = busy,
  editorConnected,
  onSubmit,
  onRetrySave,
  reviewRetry,
  blockedReason = null,
  solution = null,
  pendingSubmissionId = null,
  submissionError = null,
}: {
  task: WorkshopSession['task'];
  workspace: WorkshopSession['workspace'];
  evidence: ReturnType<typeof useWorkshopEvidence>;
  draft: ReturnType<typeof useExplanationDraft>;
  busy: boolean;
  draftBlocked?: boolean;
  editorConnected: boolean;
  onSubmit: () => void;
  onRetrySave: () => void;
  reviewRetry?: { pending: boolean; error: string | null; run: (submissionId: string) => void };
  blockedReason?: string | null;
  /** The revealed reference, fetched by the page once the policy permits it. */
  solution?: SolutionQuery | null;
  /** A capture that succeeded but whose submission the history has not listed yet. */
  pendingSubmissionId?: string | null;
  submissionError?: string | null;
}) {
  const current = evidence.data?.submissions[0];
  const policy = evidence.data?.policy ?? null;
  const [revising, setRevising] = useState<string | null>(null);
  const explanation = useRef<HTMLTextAreaElement>(null);
  const pending = current && ['queued', 'evaluating'].includes(current.status);
  const awaitingListing = Boolean(
    pendingSubmissionId &&
    !evidence.data?.submissions.some((submission) => submission.id === pendingSubmissionId),
  );
  const historyUnavailable = !evidence.data;
  const complete = current?.status === 'passed' || policy?.lockReason === 'passed';
  const locked = Boolean(policy?.locked) || complete;
  const showForm = !current || (!pending && !locked && revising === current.id);
  const disabledReason = historyUnavailable
    ? evidence.error
      ? 'Reload submission history before submitting. Your explanation is kept here.'
      : 'Wait for your saved submissions to load before submitting.'
    : (blockedReason ??
      (busy
        ? 'Wait for the current workspace operation to finish.'
        : draft.status === 'conflict'
          ? 'Resolve the explanation conflict below before submitting.'
          : locked
            ? complete
              ? 'This workshop is already complete.'
              : `You have used all ${policy?.limit ?? 0} submissions for this workshop. The reference solution is shown below.`
            : awaitingListing
              ? 'Recording your submission…'
              : workspace?.state !== 'ready'
                ? 'Wait for your coding environment to be ready. You can write your explanation now.'
                : !editorConnected
                  ? 'Reconnect the IDE with Refresh IDE before submitting. You can still edit your explanation.'
                  : draft.text.trim().length < 20
                    ? 'Add at least 20 characters explaining your approach, checks, and a limitation.'
                    : pending
                      ? 'Wait for the current review before submitting again.'
                      : null));
  const canSubmit = disabledReason === null;
  const submissionNumber = policy ? Math.min(policy.used + 1, policy.limit) : null;
  const lastSubmission = Boolean(policy && !locked && policy.remaining === 1);

  // Announce outcomes for readers who are not watching this panel (focus mode, Studio, screen readers).
  const reviewIdentity = `${workspace?.activeAttemptId ?? ''}:${current?.id ?? ''}`;
  const [announcement, setAnnouncement] = useState<{
    identity: string;
    text: string | null;
  } | null>(null);
  const previousReview = useRef<{ identity: string; status: string | null } | null>(null);
  useEffect(() => {
    const status = current?.status ?? null;
    const wasPending =
      previousReview.current?.identity === reviewIdentity &&
      ['queued', 'evaluating'].includes(previousReview.current.status ?? '');
    if (previousReview.current?.identity !== reviewIdentity || pending) setAnnouncement(null);
    else if (wasPending && current)
      setAnnouncement({ identity: reviewIdentity, text: outcomeAnnouncement(current) });
    previousReview.current = { identity: reviewIdentity, status };
  }, [current, pending, reviewIdentity]);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!pending) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [pending]);
  const waitedSeconds = current ? (now - new Date(current.createdAt).getTime()) / 1000 : 0;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (canSubmit) onSubmit();
  }
  return (
    <section aria-label="Workshop assessment">
      <p role="status" aria-live="polite" className="sr-only">
        {announcement?.identity === reviewIdentity ? announcement.text : null}
      </p>
      {task?.mode === 'unaided' ? (
        <p className="text-fg-muted text-sm">
          This is an unaided checkpoint. Mio can clarify instructions or help with workspace access.
        </p>
      ) : null}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-fg text-lg font-semibold tracking-tight">
          {pending
            ? 'Your submission is in review'
            : complete
              ? 'Workshop complete'
              : current?.status === 'revision_requested'
                ? locked
                  ? 'Review complete: no submissions left'
                  : 'Review complete: revision requested'
                : current?.status === 'failed'
                  ? 'Review interrupted'
                  : 'Submit your work'}
        </h2>
        {policy ? (
          <Pill tone={locked ? 'warning' : 'neutral'} title="Reviewed submissions used">
            Submissions used: {policy.used} of {policy.limit}
          </Pill>
        ) : null}
      </div>
      {current && !pending ? (
        <p className="text-fg-muted mt-2 text-[13px] leading-6">
          {complete
            ? 'You have completed this workshop. Review your feedback, compare with the reference solution, or continue to the next activity.'
            : current.status === 'revision_requested'
              ? locked
                ? 'Read the feedback and the reference solution below. Submitting is disabled for this workshop; you can keep editing and testing your code.'
                : 'Read the feedback, improve your solution in the editor, then revise your explanation.'
              : reviewFailureMessage(current.errorCode)}
        </p>
      ) : null}
      {policy?.solutionRevealed && solution && !pending ? (
        <Button
          variant="secondary"
          size="sm"
          className="mt-3"
          onClick={() => {
            const target = document.getElementById('reference-solution');
            if (target) scrollWithinPane(target, { block: 'start', behavior: 'smooth' });
            target?.focus({ preventScroll: true });
          }}
        >
          Jump to the reference solution
        </Button>
      ) : null}
      {evidence.isPending ? (
        <div className="border-line bg-sidebar mt-5 space-y-3 rounded-lg border p-4">
          <LoadingStatus label="Loading your submission history…" className="text-xs" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      ) : null}
      {pending ? (
        <div className="border-accent-line bg-accent-soft mt-5 space-y-3 rounded-lg border p-4">
          <LoadingStatus
            label={
              current.status === 'queued'
                ? current.errorCode
                  ? 'The review will retry automatically…'
                  : 'Submission received · waiting for review…'
                : 'Mio is reviewing your submission…'
            }
            className="text-accent text-xs"
          />
          <ProgressBar value={null} label="Submission review" />
          <p className="text-fg-muted text-xs leading-5">
            Waiting {elapsedLabel(current.createdAt, now)}. Your submitted files are saved. You can
            continue learning while the review runs; an independent review usually takes a few
            minutes.
            {waitedSeconds > 180
              ? ' This is taking longer than usual. Temporary failures are retried automatically; if nothing changes after several more minutes, check the status or contact support.'
              : ''}
          </p>
          <Button
            variant="secondary"
            loading={evidence.isFetching}
            loadingLabel="Checking review…"
            onClick={() => void evidence.refetch()}
          >
            Check review status
          </Button>
        </div>
      ) : null}
      {current?.status === 'failed' &&
      reviewRetry &&
      (current.retryable ?? retryableWorkshopReviewErrors.has(current.errorCode ?? '')) ? (
        <div className="mt-4 space-y-2">
          <Button
            loading={reviewRetry.pending}
            loadingLabel="Requesting review…"
            disabled={busy || Boolean(evidence.error)}
            onClick={() => reviewRetry.run(current.id)}
          >
            Retry saved submission
          </Button>
          <p className="text-fg-muted text-xs">
            Uses the files and explanation you already submitted. Your current editor changes stay
            separate.
          </p>
        </div>
      ) : null}
      {reviewRetry?.error ? (
        <div className="mt-3">
          <Alert>{reviewRetry.error}</Alert>
        </div>
      ) : null}
      {current && !pending && !locked && !showForm ? (
        <Button
          className="mt-5"
          disabled={busy}
          onClick={() => {
            setRevising(current.id);
            setAnnouncement(null);
            requestAnimationFrame(() => {
              const target = explanation.current;
              if (target) scrollWithinPane(target, { block: 'nearest' });
              target?.focus({ preventScroll: true });
            });
          }}
        >
          {current.status === 'revision_requested'
            ? 'Revise and resubmit'
            : 'Prepare another submission'}
        </Button>
      ) : null}
      {showForm ? (
        <form onSubmit={submit} className="mt-5 space-y-3">
          {current ? <h3 className="text-fg font-medium">Revised explanation</h3> : null}
          <p className="text-fg-muted text-[13px] leading-6">
            Explain your approach, how you checked it, and one limitation. After confirmation, your
            editor files and explanation will be saved together for review.
          </p>
          <label htmlFor="workshop-explanation" className="text-fg block text-sm">
            Your explanation
          </label>
          <textarea
            ref={explanation}
            id="workshop-explanation"
            maxLength={8000}
            value={draft.text}
            onChange={(event) => draft.edit(event.target.value)}
            disabled={draftBlocked}
            placeholder="What did you change? How did you verify it? What is one limitation?"
            aria-describedby="workshop-explanation-hint workshop-submit-reason"
            className="border-line bg-canvas text-fg focus-visible:border-line-strong focus-visible:ring-ring min-h-48 w-full resize-y border p-3 text-[13px] leading-6 outline-none focus-visible:ring-1"
          />
          <p id="workshop-explanation-hint" className="text-fg-muted text-xs">
            At least 20 characters. Your explanation saves automatically.
          </p>
          <div
            id="workshop-submit-reason"
            className="text-fg-muted text-xs leading-5"
            aria-live="polite"
          >
            {disabledReason}
          </div>
          {submissionError ? <Alert>{submissionError}</Alert> : null}
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="submit"
              loading={busy}
              loadingLabel="Updating workspace…"
              disabled={!canSubmit}
            >
              Review and submit
            </Button>
            {current ? (
              <Button variant="ghost" disabled={busy} onClick={() => setRevising(null)}>
                Back to review
              </Button>
            ) : null}
            {policy && submissionNumber ? (
              <span className="text-fg-muted text-xs" data-testid="submission-counter">
                Submission {submissionNumber} of {policy.limit}
                {lastSubmission
                  ? ' · your last one: the reference solution is shown after this review'
                  : ''}
              </span>
            ) : null}
          </div>
        </form>
      ) : null}
      {evidence.data?.submissions.length ? (
        <WorkshopReviewHistory
          key={current?.id}
          submissions={evidence.data.submissions}
          heading="Review and submitted work"
          showLatestStatus={false}
        />
      ) : null}
      {policy?.solutionRevealed && solution && !pending ? (
        <WorkshopSolution policy={policy} solution={solution} />
      ) : null}
      {evidence.hasNextPage ? (
        <Button
          variant="secondary"
          className="mt-4"
          loading={evidence.isFetchingNextPage}
          loadingLabel="Loading older submissions…"
          onClick={() => void evidence.fetchNextPage()}
        >
          Load older submissions
        </Button>
      ) : null}
      {showForm && draft.status === 'saving' ? (
        <LoadingStatus label="Saving explanation…" className="mt-3 text-xs" />
      ) : showForm ? (
        <p className="text-fg-muted mt-3 text-xs" role="status">
          {draftStatusLabel(draft)}
        </p>
      ) : null}
      {draft.error ? (
        <div role="alert" className="text-danger mt-3 text-sm">
          <p>{draft.error}</p>
          {draft.status === 'conflict' ? (
            <div className="mt-2 flex gap-2">
              <Button
                variant="secondary"
                disabled={draftBlocked}
                onClick={() => draft.resolveConflict(true)}
              >
                Keep my explanation
              </Button>
              <Button
                variant="secondary"
                disabled={draftBlocked}
                onClick={() => draft.resolveConflict(false)}
              >
                Use saved explanation
              </Button>
            </div>
          ) : (
            <Button variant="secondary" disabled={draftBlocked} onClick={onRetrySave}>
              Retry saving explanation
            </Button>
          )}
        </div>
      ) : null}
      {evidence.error ? (
        <div className="mt-4 space-y-3">
          <Alert>
            {historyUnavailable
              ? 'We could not load your submission history. Retry before submitting; your explanation is still editable.'
              : 'We could not refresh your submission history. Your last loaded feedback is shown.'}
          </Alert>
          <Button
            variant="secondary"
            loading={evidence.isFetching}
            loadingLabel="Loading submissions…"
            onClick={() => void evidence.refetch()}
          >
            Retry submission history
          </Button>
        </div>
      ) : null}
    </section>
  );
}
